import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { finalizePendingShopifyInstall, getPendingShopifyContext } from "@/lib/shopifyInstall";
import { buildEmbeddedAppPath, setShopifyEmbeddedContext } from "@/lib/shopifyEmbedded";

const SESSION_KEY = "shakatah_customer_session";

const OAuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [isShopifyOAuth, setIsShopifyOAuth] = useState(false);

  // Shopify params
  const shop = searchParams.get("shop") || "";
  const shopName = searchParams.get("shop_name") || shop;
  const merchantId = searchParams.get("merchant_id") || "";
  const embeddedHost = searchParams.get("host") || "";
  const isEmbedded = searchParams.get("embedded") === "1" || Boolean(embeddedHost);

  // Check if this is a Shopify OAuth callback
  useEffect(() => {
    if (shop || embeddedHost) {
      setShopifyEmbeddedContext({
        embedded: isEmbedded,
        host: embeddedHost,
        shop,
      });
    }

    if (shop && merchantId) {
      setIsShopifyOAuth(true);
      // Notify parent window of success (parent already has dashboard open)
      if (window.opener) {
        window.opener.postMessage(
          { type: "shopify_oauth_success", shop, merchantId },
          "*"
        );
      }
      setIsProcessing(false);
      return;
    }

    // Google OAuth flow - handle auth callback
    handleGoogleAuthCallback();
  }, [embeddedHost, isEmbedded, merchantId, shop]);

  const handleGoogleAuthCallback = async () => {
    try {
      // Get the current session after OAuth redirect
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error("OAuth session error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login", { replace: true });
        return;
      }

      if (!session?.user) {
        console.log("No session found after OAuth");
        navigate("/login", { replace: true });
        return;
      }

      const userEmail = session.user.email;
      console.log("Google OAuth successful for:", userEmail);

      // Get the user type they selected before OAuth
      // Check both sessionStorage and localStorage (sessionStorage can be lost during cross-origin redirects)
      const oauthUserType = sessionStorage.getItem("oauth_user_type") || localStorage.getItem("oauth_user_type") || "customer";
      sessionStorage.removeItem("oauth_user_type");
      localStorage.removeItem("oauth_user_type");

      // Check what type of user this is
      // 1. Check if they're a merchant user
      const { data: merchantUsers } = await supabase
        .from("merchant_users")
        .select("merchant_id, role, merchants!inner(platform_type, onboarding_completed_at, shopify_store_domain)")
        .eq("user_id", session.user.id);

      if (merchantUsers && merchantUsers.length > 0) {
        localStorage.setItem("rememberMe", "true");

        // Find the merchant
        const bizMerchant = merchantUsers[0];

        if (bizMerchant) {
          const merchant = bizMerchant.merchants as any;
          if (!merchant?.onboarding_completed_at) {
            navigate(buildEmbeddedAppPath("/merchant/onboarding"), { replace: true });
          } else {
            navigate(buildEmbeddedAppPath("/merchant-dashboard"), { replace: true });
          }
          return;
        }
      }

      // 2. Check for pending Shopify data - create merchant if found
      const pendingContext = getPendingShopifyContext();

      if (pendingContext.shop && pendingContext.sessionToken) {
        console.log("Finalizing Shopify install for Google OAuth user:", pendingContext.shop);

        try {
          await finalizePendingShopifyInstall(pendingContext.sessionToken);
          localStorage.setItem("rememberMe", "true");
          toast.success("Shopify store connected! Let's complete your setup.");
          navigate(buildEmbeddedAppPath("/merchant/onboarding"), { replace: true });
          return;
        } catch (installError) {
          console.error("Failed to finalize Shopify install:", installError);
        }
      }

      // 3. Check if this email exists in any customer-facing activity
      const { data: customerActivity } = await supabase
        .from("gift_reminders")
        .select("id")
        .or(`customer_email.eq.${userEmail},reminder_email.eq.${userEmail},invited_friend_email.eq.${userEmail}`)
        .limit(1)
        .maybeSingle();

      const { data: purchaseActivity } = await supabase
        .from("group_purchases")
        .select("id")
        .eq("organizer_email", userEmail)
        .limit(1)
        .maybeSingle();

      const { data: participantActivity } = await supabase
        .from("group_purchase_participants")
        .select("id")
        .eq("email", userEmail)
        .limit(1)
        .maybeSingle();

      // If they have customer activity, set up customer session and redirect
      if (customerActivity || purchaseActivity || participantActivity) {
        // Create customer session
        const customerSession = {
          email: userEmail,
          phone: null,
          method: "google",
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(customerSession));
        toast.success("Welcome! Redirecting to your dashboard...");
        navigate("/my-purchases", { replace: true });
        return;
      }

      // 4. New user - they likely need to sign up
      // For Google OAuth, we'll ask them to complete signup
      toast.info("Welcome! Please complete your account setup.");
      navigate("/login?tab=signup", { replace: true });

    } catch (err) {
      console.error("OAuth callback error:", err);
      toast.error("Something went wrong. Please try again.");
      navigate("/login", { replace: true });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    window.close();
  };

  // Shopify OAuth success view
  if (isShopifyOAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <div className="text-center p-12 max-w-md">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-20 w-20 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">
            Store Connected Successfully!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Your Shopify store <span className="font-semibold text-foreground">{shopName}</span> has been reconnected.
          </p>
          <Button onClick={handleClose} size="lg" className="px-8">
            Close Window
          </Button>
        </div>
      </div>
    );
  }

  // Google OAuth processing view
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
      <div className="text-center p-12 max-w-md">
        {isProcessing ? (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Completing Sign In...
            </h1>
            <p className="text-muted-foreground">
              Please wait while we set up your account.
            </p>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-6">
              <CheckCircle className="h-20 w-20 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Sign In Successful!
            </h1>
            <p className="text-muted-foreground mb-6">
              Redirecting you to your dashboard...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthSuccess;
